/**
 * Unified grid display name / symbol / pair label / image resolution.
 *
 * Every surface that needs to display a grid's identity (markets table, claim
 * dropdown, single grid page, SEO meta, admin pages) should call
 * `resolveGridDisplay()` instead of re-deriving these values locally.
 */

import { parseGridId } from './utils';
import { HV_TAKEOVER_GRID_ID } from './constants/addresses';

/**
 * Clamp a grid's `activeSeatCount` against its `totalSeats`.
 *
 * The Envio indexer's `activeSeatCount` is decremented when seats are
 * cleared, but during a chain reorg the counter can briefly read above
 * `totalSeats` (a re-mined "claim" before its compensating "abandon").
 * The lobby card has clamped the value via this exact `Math.max(0, …)`
 * + `Math.min(…, totalSeats)` pair since the V2 launch; the admin
 * Boards tab and any other surface that renders the seat fraction
 * should call this instead of using the raw value, otherwise a
 * 100-seat board can read "103/100" until the indexer settles.
 *
 * Structural typing on a `Pick` of the two fields so any caller with
 * a `LobbyGridSummary`-shaped object (`/api/v1/grids` response, the
 * SDK's `LobbyGridSummary`, the admin tab's stripped local interface)
 * passes through without coupling this helper to a specific module.
 */
export function taxPayingSeatCount(
  g: { activeSeatCount: number; totalSeats: number },
): number {
  return Math.max(0, Math.min(g.activeSeatCount, g.totalSeats));
}

// Well-known onchain addresses (lowercase)
export const KNOWN_TOKEN_SYMBOLS: Record<string, string> = {
  '0x4200000000000000000000000000000000000006': 'WETH',
  '0x4200000000000000000000000000000000000001': 'WETH',
  '0x0000000000000000000000000000000000000000': 'ETH',
  '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913': 'USDC',
};

const BASE_TOKEN_SYMBOLS = new Set([
  'ETH', 'WETH', 'FLETH', 'USDC', 'USDT',
]);

function isBaseToken(symbol: string): boolean {
  return BASE_TOKEN_SYMBOLS.has(symbol.toUpperCase());
}

const GENERIC_NAME_RE = /^(Grid\s*#?\d+|Grid\s*#?0x[a-f0-9]+-\d+|G\d+)$/i;

/**
 * True when `name` looks like one of the synthesized placeholders we use
 * before a creator has identity info on file (e.g. `"Grid #4"`, `"G4"`).
 * Exported so consumers that merge several name sources (Envio asset
 * symbols, onchain `Grid.name`, off-chain `pool_metadata.name`) can
 * skip a placeholder when a real name is available.
 */
export function isGenericName(name: string): boolean {
  return GENERIC_NAME_RE.test(name.trim());
}

function isPairName(name: string): boolean {
  return /\//.test(name.trim());
}

// ─── Public types ────────────────────────────────────────────────────────────

export interface GridAssetInput {
  assetType: string | null;
  token0Symbol: string | null;
  token1Symbol: string | null;
  token0Address: string | null;
  token1Address: string | null;
  flaunchAddress?: string | null;
}

export interface GridDisplayInput {
  gridId: string;
  assets: GridAssetInput[];
  yieldToken: string;
  /** Metadata name — ideally the creator-chosen display ticker */
  metaName?: string | null;
  /** Metadata image URL */
  metaImageUrl?: string | null;
}

export interface GridDisplayInfo {
  /** Single display ticker, e.g. "BANKR" */
  displayName: string;
  /** Short symbol — same as displayName in most cases */
  symbol: string;
  /** Full pair label, e.g. "BANKR/ETH LP" */
  pairLabel: string | undefined;
  /** Resolved image URL */
  imageUrl: string | undefined;
  /** Adapter type */
  adapterType: 'flaunch' | 'uniswap-v3' | 'uniswap-v4' | 'aerodrome' | undefined;
  /** Address of the non-yield (display) token */
  displayTokenAddress: string | undefined;
}

// ─── Core resolver ───────────────────────────────────────────────────────────

export function resolveGridDisplay(input: GridDisplayInput): GridDisplayInfo {
  const { gridId, assets, metaName, metaImageUrl } = input;

  if (gridId === HV_TAKEOVER_GRID_ID) {
    return {
      displayName: 'Boardroom',
      symbol: 'BOARDROOM',
      pairLabel: undefined,
      imageUrl: '/boardroom.png',
      adapterType: undefined,
      displayTokenAddress: undefined,
    };
  }

  const yieldToken = (input.yieldToken || '0x4200000000000000000000000000000000000006').toLowerCase();
  const effectiveYield = yieldToken === '0x0000000000000000000000000000000000000000'
    ? '0x4200000000000000000000000000000000000006'
    : yieldToken;

  const primaryAsset = assets[0] ?? null;
  const { numericId } = parseGridId(gridId);

  let displayName = `Grid #${numericId}`;
  let symbol = `G${numericId}`;
  let pairLabel: string | undefined;
  let adapterType: GridDisplayInfo['adapterType'];
  let displayTokenAddress: string | undefined;

  if (primaryAsset) {
    const t0Addr = (primaryAsset.token0Address || '').toLowerCase();
    const t1Addr = (primaryAsset.token1Address || '').toLowerCase();
    const t0 = primaryAsset.token0Symbol || KNOWN_TOKEN_SYMBOLS[t0Addr] || '';
    const t1 = primaryAsset.token1Symbol || KNOWN_TOKEN_SYMBOLS[t1Addr] || '';

    if (
      primaryAsset.assetType === 'flaunch' ||
      primaryAsset.assetType === 'lp' ||
      primaryAsset.assetType === 'lp-v4'
    ) {
      adapterType = primaryAsset.assetType === 'flaunch'
        ? 'flaunch'
        : primaryAsset.assetType === 'lp-v4' ? 'uniswap-v4' : 'uniswap-v3';

      // Pick the non-base token as displayName
      const t0IsBase = t0 ? isBaseToken(t0) : false;
      const t1IsBase = t1 ? isBaseToken(t1) : false;

      if (t0IsBase && !t1IsBase && t1) {
        displayName = t1;
        symbol = t1;
        displayTokenAddress = t1Addr || undefined;
        pairLabel = t0 && t1 ? `${t1}/${t0} LP` : undefined;
      } else if (t1IsBase && !t0IsBase && t0) {
        displayName = t0;
        symbol = t0;
        displayTokenAddress = t0Addr || undefined;
        pairLabel = t0 && t1 ? `${t0}/${t1} LP` : undefined;
      } else if (t0 && t1) {
        displayName = t0;
        symbol = t0;
        displayTokenAddress = t0Addr || undefined;
        pairLabel = `${t0}/${t1} LP`;
      } else {
        if (t0Addr && t0Addr !== effectiveYield) displayTokenAddress = t0Addr;
        else if (t1Addr && t1Addr !== effectiveYield) displayTokenAddress = t1Addr;
        const label = t0 || t1;
        if (label) pairLabel = `${label}/ETH LP`;
      }

    } else {
      // Unknown asset type
      adapterType = primaryAsset.assetType === 'aerodrome' ? 'aerodrome' : undefined;
      const joined = [t0, t1].filter(Boolean).join('/');
      if (joined) {
        displayName = joined;
        symbol = t0 || symbol;
      }
      pairLabel = joined || undefined;
      if (t0Addr && t0Addr !== effectiveYield) displayTokenAddress = t0Addr;
      else if (t1Addr && t1Addr !== effectiveYield) displayTokenAddress = t1Addr;
    }
  }

  // Creator-set metadata name wins over the asset-derived ticker for
  // `displayName`: a board's identity can differ from its yield-source
  // token (e.g. "PotWars" on a TAKEOVER/USDC LP — grid 43 showed as
  // "TAKEOVER" in the lobby under the old fallback-only rule). `symbol`
  // and `pairLabel` stay asset-derived so ticker chips and pair labels
  // keep pointing at the tradable token; `symbol` only takes the
  // metaName when no asset produced a ticker. Placeholder-shaped
  // metaNames ("Grid #4") are still ignored so the synthesized fallback
  // applies. The metaName reaching this resolver comes exclusively from
  // the creator/admin edit endpoint (`gridMetaKey` namespace), and
  // listing surfaces run `isBlockedName()` on the resolved displayName
  // (see /api/v1/grids), so impersonation moderation still applies.
  if (metaName && !isGenericName(metaName)) {
    displayName = metaName;
    if (symbol === `G${numericId}`) symbol = metaName;
  }

  // Image: for Flaunch grids prefer metadata (IPFS); for LP grids prefer token-icon API
  const imageUrl = adapterType === 'flaunch'
    ? (metaImageUrl || (displayTokenAddress ? `/api/token-icon/${displayTokenAddress}` : undefined))
    : (displayTokenAddress ? `/api/token-icon/${displayTokenAddress}` : (metaImageUrl ?? undefined));

  return { displayName, symbol, pairLabel, imageUrl, adapterType, displayTokenAddress };
}
